import emit from '../../../common/events';
import store from '../../../store/store';
import { FT_CONVERT_INTL } from './free-trial-conversion.util';
import { Modal } from '../../modal/modal';
import { Product } from '../../cart/shop-cart/interfaces/shop-cart.interface';
import { RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { getBillingAddress, resetPaymentDetails } from '../../checkout/shop-checkout';
import { getCartPageUrl } from '../../../common/utils/cart-util';
import { getFreeTrialProduct } from './free-trial.util';
import { html } from 'lit';
import { selectUser } from '../../user-profile/user-profile.selectors';

export default class FreeTrialCountryRestriction {
  /**
   * Default country code.
   */
  private readonly defaultCountryCode = 'US';

  /**
   * Modal instance for free trial country restriction.
   */
  private readonly modal: Modal;

  /**
   * Identifier of the modal.
   */
  private readonly componentRef = 'free-trial-country-restriction';

  constructor() {
    this.modal = new Modal({
      header: 'Free trials are only available to U.S. customers.',
      body: {template: this.getCountryRestrictionsModalTemplate()},
      modalContext: {componentRef: this.componentRef},
      primaryButton: {label: 'Return to Cart', ariaLabel: 'Return to Cart'},
      secondaryButton: {label: 'Update Billing Info', ariaLabel: 'Update Billing Info'},
      showClosingCross: false
    });
  }

  /**
   * Verifies the country of the billing address.
   *
   * If there are some free trial products in the cart and the country is not US, it will return false.
   */
  validate(): boolean {
    const state = store.getState() as StoreState;
    return !this.isFreeTrialRestricted(getBillingAddress(state), getFreeTrialProduct());
  }

  /**
   * Waits for the modal actions to be resolved.
   *
   * If the modal is confirmed, the promise is rejected to prevent further checkout actions.
   *
   * The user is then redirected to the cart page.
   */
  async waitForModal(): Promise<void> {
    this.modal.open();

    return new Promise((resolve, reject) => {
      this.modal.addEventListener(
        'mhecomm-modal-confirmed',
        () => {
          store.dispatch(resetPaymentDetails());
          this.redirectToCart();
          reject();
        }
      );
      this.modal.addEventListener(
        'country-restriction-modal-modal-close',
        () => resolve()
      );
    });
  }

  /**
   * Checks if the free trial is restricted for the country indicated on the billing address.
   *
   * @param billingAddress
   * @param freeTrialProduct
   */
  isFreeTrialRestricted(billingAddress?: RmsBookstoreAddress, freeTrialProduct?: Product): boolean {
    return !!freeTrialProduct && !!billingAddress && billingAddress.country !== this.defaultCountryCode;
  }

  /**
   * Redirects the user to the cart page.
   */
  private redirectToCart(): void {
    const cartPageUrl = this.buildCartPageUrl(store.getState() as StoreState);

    this.onClickReturnToCart(cartPageUrl);
    window.location.href = cartPageUrl;
  }

  /**
   * Builds the cart page URL with the free trial conversion for international flag.
   *
   * @param state
   * @private
   */
  private buildCartPageUrl(state: StoreState): string {
    const cartPageUrl = new URL(getCartPageUrl(selectUser(state)));
    cartPageUrl.searchParams.set(FT_CONVERT_INTL, 'true');

    return cartPageUrl.toString();
  }

  /**
   * Template for the country restrictions modal.
   *
   * @private
   */
  private getCountryRestrictionsModalTemplate() {
    return html`
      <p>
        Your billing address indicates that you’re outside of the U.S. If this is incorrect, you can change your billing
        address to a U.S. address to continue with a free trial. If you are using a digital wallet or payment platform
        such as PayPal, Apple Pay or Google Pay, your billing address will need to be updated within that service.
      </p>
      <p>
        Otherwise, return to the cart and start the checkout process again. You will be charged the price of your
        selected subscription length.
      </p>
    `;
  }

  /**
   * Event handler for the return to cart button click.
   *
   * It is emitted in addition to the close modal event, to track that the user wants to return to the cart.
   *
   * @param cartPageUrl
   * @private
   */
  private onClickReturnToCart(cartPageUrl: string): void {
    emit({
      name: 'click',
      element: this.modal,
      detail: {
        eventName: `${this.componentRef}-return-to-cart`,
        label: 'Return to Cart',
        url: cartPageUrl
      },
    })
  }
}
